import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import { Delete, Upload, AutoAwesome } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { blogService } from "../../services/api";
import { toast } from "react-toastify";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://backendapi.vinautochecker.com";
// Validation Schema
const validationSchema = Yup.object({
  title: Yup.string()
    .min(10, "Title must be at least 10 characters")
    .max(200, "Title must be at most 200 characters")
    .required("Title is required"),
  meta_title: Yup.string().max(
    100,
    "Meta title should be under 100 characters"
  ),
  meta_description: Yup.string().max(
    200,
    "Meta description should be under 200 characters"
  ),
  content: Yup.string()
    .min(100, "Content must be at least 100 characters")
    .required("Content is required"),
});

const PostCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [publishToWordpress, setPublishToWordpress] = useState(false);
  const [ckeditorLoaded, setCkeditorLoaded] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(isEdit);
  const [editorReady, setEditorReady] = useState(false);
  const [postDataLoaded, setPostDataLoaded] = useState(!isEdit);

  const editorRef = useRef(null);
  const editorInstance = useRef(null);
  const shouldSetContent = useRef(false);

  // Formik
  const formik = useFormik({
    initialValues: {
      title: "",
      meta_title: "",
      meta_description: "",
      content: "",
      featured_image: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Get latest content from editor before submit
        if (editorInstance.current) {
          try {
            values.content = editorInstance.current.getData();
          } catch (e) {
            console.error("Error getting editor data:", e);
          }
        }

        const postData = {
          ...values,
          featured_image: selectedImage,
          publish_to_wordpress: publishToWordpress,
        };

        if (isEdit) {
          await blogService.updatePost(id, postData);
          toast.success("Post updated successfully!");
        } else {
          await blogService.createPost(postData);
          toast.success("Post created successfully!");
        }

        // Destroy editor before navigation
        if (editorInstance.current) {
          try {
            editorInstance.current.destroy(true);
            editorInstance.current = null;
          } catch (e) {
            console.error("Error destroying editor:", e);
          }
        }

        navigate("/dashboard");
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to save post");
      } finally {
        setLoading(false);
      }
    },
  });

  // FETCH DATA IF EDITING
  useEffect(() => {
    if (!isEdit) return;

    const loadPost = async () => {
      try {
        setIsFetchingData(true);
        const res = await blogService.getPost(id);
        const post = res.data.data;
        setPublishToWordpress(post.status === "draft");

        formik.setValues({
          title: post.title || "",
          meta_title: post.meta_title || "",
          meta_description: post.meta_description || "",
          content: post.content || "",
          featured_image: post.featured_image || "",
        });

        if (post.featured_image) {
          const fullUrl = `${API_BASE_URL}/${post.featured_image.replace(
            /^\//,
            ""
          )}`;
          setSelectedImage(fullUrl);
          setImagePreview(fullUrl);          
        }

        setPostDataLoaded(true);
        shouldSetContent.current = true;

        // If editor is already ready, set content now
        if (editorInstance.current && post.content) {
          setTimeout(() => {
            try {
              editorInstance.current.setData(post.content);
              shouldSetContent.current = false;
            } catch (e) {
              console.error("Error setting content:", e);
            }
          }, 500);
        }
      } catch (err) {
        console.error("Failed to load post:", err);
        toast.error("Failed to load post");
        setPostDataLoaded(true);
      } finally {
        setIsFetchingData(false);
      }
    };

    loadPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // Load CKEditor from CDN
  useEffect(() => {
    if (window.CKEDITOR) {
      setCkeditorLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.ckeditor.com/4.16.2/standard/ckeditor.js";
    script.async = false; // Changed to false for synchronous loading

    script.onload = () => {
     // console.log("✅ CKEditor loaded");
      setCkeditorLoaded(true);
    };

    script.onerror = () => {
      console.error("❌ Failed to load CKEditor");
      toast.error("Failed to load text editor. Please refresh.");
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // ✅ FIXED - Initialize CKEditor (NO FORMIK IN DEPENDENCIES)
  useEffect(() => {
    // Wait for all conditions
    if (!ckeditorLoaded || !postDataLoaded || !editorRef.current) {
      return;
    }

    // Prevent multiple initializations
    if (editorInstance.current) {
      return;
    }

    // Small delay to ensure DOM is fully ready
    const initTimer = setTimeout(() => {
      try {
        const textarea = editorRef.current;

        // Check if textarea exists and is in the DOM
        if (!textarea || !document.body.contains(textarea)) {
          console.error("Textarea not in DOM");
          return;
        }

        // Generate unique ID
        const editorId = `ckeditor-${Date.now()}`;
        textarea.setAttribute("id", editorId);

        //console.log("🚀 Initializing CKEditor with ID:", editorId);

        // Destroy any existing instance
        if (window.CKEDITOR.instances[editorId]) {
          window.CKEDITOR.instances[editorId].destroy(true);
        }

        // Create editor instance
        const editor = window.CKEDITOR.replace(editorId, {
          height: 400,
          toolbar: [
            { name: "document", items: ["Source"] },
            { name: "clipboard", items: ["Undo", "Redo"] },
            {
              name: "basicstyles",
              items: ["Bold", "Italic", "Underline", "Strike"],
            },
            {
              name: "paragraph",
              items: [
                "NumberedList",
                "BulletedList",
                "-",
                "JustifyLeft",
                "JustifyCenter",
                "JustifyRight",
              ],
            },
            { name: "links", items: ["Link", "Unlink"] },
            { name: "insert", items: ["Image", "Table"] },
          ],
          allowedContent: true,
        });

        if (!editor) {
          console.error("Failed to create editor instance");
          return;
        }

        // Store instance
        editorInstance.current = editor;

        // Wait for editor to be ready
        editor.on("instanceReady", function (evt) {
         // console.log("✅ CKEditor ready!");
          setEditorReady(true);

          // Set initial content if exists
          const initialContent = formik.values.content;
          if (initialContent && shouldSetContent.current) {
            //console.log("Setting initial content");
            setTimeout(() => {
              try {
                evt.editor.setData(initialContent);
                shouldSetContent.current = false;
              } catch (e) {
                console.error("Error setting initial data:", e);
              }
            }, 100);
          }

          // Listen for changes
          evt.editor.on("change", function () {
            const data = evt.editor.getData();
            formik.setFieldValue("content", data);
          });

          evt.editor.on("blur", function () {
            const data = evt.editor.getData();
            formik.setFieldValue("content", data);
            formik.setFieldTouched("content", true);
          });
        });

        // Error handling
        editor.on("error", function (evt) {
          console.error("CKEditor error:", evt);
        });
      } catch (error) {
        console.error("Error initializing CKEditor:", error);
        toast.error("Failed to initialize editor");
      }
    }, 500);

    // Cleanup
    return () => {
      clearTimeout(initTimer);

      if (editorInstance.current) {
        try {
         // console.log("🧹 Cleaning up editor");
          editorInstance.current.destroy(true);
          editorInstance.current = null;
        } catch (e) {
          console.error("Error destroying editor:", e);
        }
      }

      setEditorReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ckeditorLoaded, postDataLoaded]);

  // Image Upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingImage(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await blogService.uploadImage(formData);
      setSelectedImage(res.data.path);
      toast.success("Image uploaded successfully");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle AI Content Generation
  const handleGenerateAIContent = async () => {
    if (!formik.values.title.trim()) {
      toast.error("Please enter a title first");
      return;
    }

    setGeneratingAI(true);
    try {
      const response = await blogService.generateAIContent(formik.values.title);
      const { meta_title, meta_description, content } = response.data;

      formik.setValues({
        ...formik.values,
        meta_title: meta_title || formik.values.meta_title,
        meta_description: meta_description || formik.values.meta_description,
        content: content || formik.values.content,
      });

      // Update CKEditor with AI content
      if (editorInstance.current && editorReady && content) {
        setTimeout(() => {
          try {
            editorInstance.current.setData(content);
          } catch (e) {
            console.error("Error setting AI content:", e);
          }
        }, 100);
      }

      toast.success("AI content generated successfully!");
    } catch {
      toast.error("Failed to generate AI content");
    } finally {
      setGeneratingAI(false);
    }
  };

  // Loading state
  if (isEdit && isFetchingData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4, textAlign: "center", py: 10 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="textSecondary">
            Loading post data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {isEdit ? "Edit Post" : "Create New Post"}
        </Typography>

        <Paper elevation={3} sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            {/* Title */}
            <TextField
              fullWidth
              label="Post Title *"
              id="title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              sx={{ mb: 3 }}
            />

            {/* Image */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Featured Image
                </Typography>
                {imagePreview && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setImagePreview("");
                      setSelectedImage(null);
                    }}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>

              {imagePreview ? (
                <Box
                  sx={{
                    width: "100%",
                    maxHeight: "300px",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ) : (
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Upload sx={{ fontSize: 48, mb: 2, color: "gray" }} />
                    <Typography variant="body2" mb={2} color="textSecondary">
                      Upload featured image (Max 5MB)
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Uploading...
                        </>
                      ) : (
                        "Choose Image"
                      )}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Box>

            {/* SEO Settings */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  SEO Settings
                </Typography>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={handleGenerateAIContent}
                  disabled={generatingAI || !editorReady}
                  sx={{ mb: 2 }}
                >
                  {generatingAI ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Generating...
                    </>
                  ) : (
                    "Generate with AI"
                  )}
                </Button>

                <TextField
                  fullWidth
                  id="meta_title"
                  name="meta_title"
                  label="Meta Title"
                  value={formik.values.meta_title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.meta_title &&
                    Boolean(formik.errors.meta_title)
                  }
                  helperText={
                    formik.touched.meta_title && formik.errors.meta_title
                      ? formik.errors.meta_title
                      : `${formik.values.meta_title.length}/100`
                  }
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  id="meta_description"
                  name="meta_description"
                  label="Meta Description"
                  multiline
                  rows={3}
                  value={formik.values.meta_description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.meta_description &&
                    Boolean(formik.errors.meta_description)
                  }
                  helperText={
                    formik.touched.meta_description &&
                    formik.errors.meta_description
                      ? formik.errors.meta_description
                      : `${formik.values.meta_description.length}/200`
                  }
                />
              </CardContent>
            </Card>

            {/* Content Editor */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                Content *
              </Typography>

              {!ckeditorLoaded || !postDataLoaded ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    border: "1px dashed #ccc",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <CircularProgress size={32} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="textSecondary">
                    {!ckeditorLoaded
                      ? "Loading text editor..."
                      : "Loading post data..."}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      minHeight: "450px",
                      bgcolor: "white",
                    }}
                  >
                    <textarea
                      ref={editorRef}
                      style={{
                        width: "100%",
                        minHeight: "400px",
                        border: "none",
                        padding: "10px",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                      }}
                      defaultValue=""
                    />
                  </Box>

                  {!editorReady && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="textSecondary">
                        Initializing editor...
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {formik.touched.content && formik.errors.content && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, ml: 2, display: "block" }}
                >
                  {formik.errors.content}
                </Typography>
              )}

              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 1, display: "block" }}
              >
                Characters:{" "}
                {formik.values.content.replace(/<[^>]*>/g, "").length}
              </Typography>
            </Box>

            {/* Publishing Options */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Publishing Options
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={publishToWordpress}
                      onChange={(e) => setPublishToWordpress(e.target.checked)}
                    />
                  }
                  label="Publish to WordPress as draft"
                />

                <Box mt={2}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || !editorReady}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        {isEdit ? "Updating..." : "Publishing..."}
                      </>
                    ) : isEdit ? (
                      "Update Post"
                    ) : (
                      "Publish Post"
                    )}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      // Destroy editor before navigation
                      if (editorInstance.current) {
                        try {
                          editorInstance.current.destroy(true);
                          editorInstance.current = null;
                        } catch (e) {
                          console.error(
                            "Error destroying editor on cancel:",
                            e
                          );
                        }
                      }
                      navigate("/dashboard");
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default PostCreate;
